/**
 * IoModelEditor — a simple nested tree editor for agent input/output models
 * (DecisionRules-style I/O model "Simple Editor").
 *
 * The component owns its tree state, initialized from `initialFields`;
 * every change is reported through `onChange`. Remount (change `key`)
 * to reset from new initial fields.
 */

import { useState } from 'react';
import { Collapse } from '@mui/material';
import {
  Plus as AddIcon,
  Trash2 as DeleteIcon,
  ChevronDown as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  CornerDownRight as AddChildIcon,
  SlidersHorizontal as DetailsIcon,
} from 'lucide-react';
import { Button, CoordioDropdown, Input, Switch, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import type { IoField, IoFieldType } from '../../utils/ioModel';
import { isValidFieldName, newFieldId } from '../../utils/ioModel';

interface IoModelEditorProps {
  mode: 'input' | 'output';
  initialFields: IoField[];
  onChange: (fields: IoField[]) => void;
  readOnly?: boolean;
}

const OUTPUT_TYPES: { value: IoFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'object', label: 'Object' },
  { value: 'array', label: 'Array' },
  { value: 'auto', label: 'Auto' },
];

const INPUT_TYPES: { value: IoFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'json', label: 'JSON' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
];

/** Applies an updater to the node with the given id anywhere in the tree. */
const updateNode = (fields: IoField[], id: string, updater: (field: IoField) => IoField): IoField[] =>
  fields.map((field) => {
    if (field.id === id) return updater(field);
    if (field.children) return { ...field, children: updateNode(field.children, id, updater) };
    return field;
  });

/** Removes the node with the given id anywhere in the tree. */
const removeNode = (fields: IoField[], id: string): IoField[] =>
  fields
    .filter((field) => field.id !== id)
    .map((field) => (field.children ? { ...field, children: removeNode(field.children, id) } : field));

/** Appends a child to the node with the given id. */
const appendChild = (fields: IoField[], parentId: string, child: IoField): IoField[] =>
  fields.map((field) => {
    if (field.id === parentId) {
      return { ...field, children: [...(field.children ?? []), child] };
    }
    if (field.children) return { ...field, children: appendChild(field.children, parentId, child) };
    return field;
  });

export const IoModelEditor = ({ mode, initialFields, onChange, readOnly = false }: IoModelEditorProps) => {
  const [fields, setFields] = useState<IoField[]>(initialFields);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [detailsOpen, setDetailsOpen] = useState<Set<string>>(new Set());

  const typeOptions = mode === 'input' ? INPUT_TYPES : OUTPUT_TYPES;

  const apply = (next: IoField[]) => {
    setFields(next);
    onChange(next);
  };

  const toggleSet = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const newField = (): IoField => ({ id: newFieldId(), name: '', type: 'text', required: false });

  const handleAddRoot = () => apply([...fields, newField()]);

  const handleAddChild = (parentId: string) => {
    apply(appendChild(fields, parentId, newField()));
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(parentId);
      return next;
    });
  };

  const handleDelete = (id: string) => apply(removeNode(fields, id));

  const handleFieldChange = (id: string, patch: Partial<IoField>) =>
    apply(updateNode(fields, id, (field) => {
      const next = { ...field, ...patch };
      if (patch.type && patch.type !== 'object' && patch.type !== 'array' && next.children) {
        delete next.children;
      }
      if (patch.type === 'object' && !next.children) next.children = [];
      return next;
    }));

  const renderRow = (field: IoField, depth: number) => {
    const isContainer = field.type === 'object' || field.type === 'array';
    const hasChildren = (field.children?.length ?? 0) > 0;
    const isCollapsed = collapsed.has(field.id);
    const showDetails = detailsOpen.has(field.id);
    const nameInvalid = field.name.length > 0 && !isValidFieldName(field.name);

    return (
      <div key={field.id}>
        <div
          className="flex items-start gap-2 py-1.5 border-b border-dashed border-[var(--color-border-default)]"
          style={{ paddingLeft: `${depth * 24}px` }}
        >
          <button
            type="button"
            className={`mt-1 p-1 rounded hover:bg-[var(--glass-bg-subtle)] ${hasChildren ? 'visible' : 'invisible'}`}
            onClick={() => setCollapsed((prev) => toggleSet(prev, field.id))}
            aria-label={isCollapsed ? 'Expand field' : 'Collapse field'}
          >
            {isCollapsed ? <ChevronRightIcon size={16} /> : <ExpandMoreIcon size={16} />}
          </button>

          <div className="flex flex-col">
            <Input
              type="text"
              placeholder="fieldName"
              value={field.name}
              onChange={(e) => handleFieldChange(field.id, { name: e.target.value })}
              disabled={readOnly}
              aria-label="Field name"
              className={`w-[200px] font-mono ${nameInvalid ? 'border-red-500' : ''}`}
            />
            {nameInvalid && <span className="text-xs text-red-500 mt-0.5">Letters, numbers, _ and - only</span>}
          </div>

          <div className="w-[120px]">
            <CoordioDropdown
              options={typeOptions}
              value={field.type}
              onChange={(val) => handleFieldChange(field.id, { type: (val ?? 'text') as IoFieldType })}
              aria-label="Field type"
              size="sm"
              clearable={false}
              disabled={readOnly}
            />
          </div>

          <Input
            type="text"
            placeholder={mode === 'output' ? 'Instruction for the model (what to return here)' : 'Description'}
            value={field.description ?? ''}
            onChange={(e) => handleFieldChange(field.id, { description: e.target.value })}
            disabled={readOnly}
            aria-label="Field description"
            className="flex-1 min-w-[160px]"
          />

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="mt-1">
                  <Switch
                    checked={field.required ?? false}
                    onCheckedChange={(checked) => handleFieldChange(field.id, { required: checked })}
                    disabled={readOnly || field.type === 'object'}
                    aria-label="Required field"
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>Required</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {mode === 'input' && field.type !== 'object' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setDetailsOpen((prev) => toggleSet(prev, field.id))}
                    className={showDetails || field.isSecret || field.defaultValue ? 'text-[var(--color-accent-primary)]' : ''}
                    aria-label="Field settings"
                  >
                    <DetailsIcon size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Default value, secret and label</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {isContainer && !readOnly && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" onClick={() => handleAddChild(field.id)} aria-label="Add child field">
                    <AddChildIcon size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Add child field</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {!readOnly && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(field.id)} aria-label="Delete field">
                    <DeleteIcon size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete field</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {mode === 'input' && (
          <Collapse in={showDetails} unmountOnExit>
            <div className="flex gap-4 items-start py-2 flex-wrap" style={{ paddingLeft: `${depth * 24 + 48}px` }}>
              <div className="w-[320px]">
                <label className="block text-sm font-medium mb-1">Label</label>
                <Input
                  type="text"
                  value={field.label ?? ''}
                  onChange={(e) => handleFieldChange(field.id, { label: e.target.value })}
                  disabled={readOnly}
                  className="w-full"
                />
                <p className="text-xs text-[var(--color-text-secondary)] mt-0.5 whitespace-nowrap">
                  Shown on the run form (defaults to the field name)
                </p>
              </div>
              <div className="w-[240px]">
                <label className="block text-sm font-medium mb-1">
                  {field.isSecret ? 'Secret value' : 'Default value'}
                </label>
                <Input
                  type={field.isSecret ? 'password' : 'text'}
                  value={field.defaultValue ?? ''}
                  onChange={(e) => handleFieldChange(field.id, { defaultValue: e.target.value })}
                  disabled={readOnly}
                  className="w-full"
                />
                {field.isSecret && (
                  <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                      {field.hasSecretConfigured
                      ? 'Leave blank to keep the current Key Vault secret'
                      : 'Stored securely in Azure Key Vault'}
                  </p>
                )}
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <label className="inline-flex items-center gap-2 mt-5 cursor-pointer text-sm font-medium">
                      <Switch
                        checked={field.isSecret ?? false}
                        onCheckedChange={(checked) => handleFieldChange(field.id, { isSecret: checked })}
                        disabled={readOnly}
                        aria-label="Secret field"
                      />
                      <span className="text-[var(--color-text-secondary)]">Secret</span>
                    </label>
                  </TooltipTrigger>
                  <TooltipContent>Secret (store in Key Vault)</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </Collapse>
        )}

        {isContainer && hasChildren && (
          <Collapse in={!isCollapsed} unmountOnExit>
            {field.children!.map((child) => renderRow(child, depth + 1))}
          </Collapse>
        )}
      </div>
    );
  };

  return (
    <div>
      {fields.length === 0 ? (
        <div className="flex items-center gap-2 p-3 mb-2 rounded-md bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-200">
          {mode === 'input'
            ? 'No input fields defined. Add fields to auto-generate an input form when this agent runs.'
            : 'No output fields defined. Add fields to require a structured JSON response.'}
        </div>
      ) : (
        <div className="border border-[var(--color-border-default)] rounded px-2">
          {fields.map((field) => renderRow(field, 0))}
        </div>
      )}
      {!readOnly && (
        <Button variant="ghost" size="sm" onClick={handleAddRoot} className="mt-2">
          <AddIcon className="mr-1" size={16} />
          Add field
        </Button>
      )}
    </div>
  );
};
